import { DateTime, DurationObjectUnits } from "luxon";

import { LuxonDateTimeParser } from "./LuxonDateTimeParser";
import { TimeInterval } from "./TimeInterval";

/**
 * A wrapper for Luxon DateTime which only tracks the date values. This may wrap a valid or invalid date; you should check 'isValid' before using other properties, which may return null or unexpected values for an invalid date.
 */
class DateOnly {
    /**
     * The underlying date, without time info, in the UTC time zone. This may be null for some invalid dates.
     */
    private readonly parsed: DateTime;

    /**
    * Construct an instance.
    * @param date The date or ISO-8601 date string.
    */
    constructor(date: DateTime | DateOnly | Date | string) {
        // parse raw date
        // Note that LuxonDateTimeParser handles invalid dates inconsistently. If the input is null or an empty string,
        // it returns null. For any other invalid input (e.g. " "), it returns a date marked invalid.
        const parsed = LuxonDateTimeParser.parse(date);
        this.parsed = parsed && DateTime.utc(parsed.year, parsed.month, parsed.day);
    }

    /**
     * Get a DateOnly instance set to today's date, utc at midnight.
     * */
    public static today(): DateOnly {
        return new DateOnly(DateTime.utc());
    }

    /**
    * Get a DateOnly instance if the value isn't null/undefined/empty, else null.
    * @param date The date or ISO-8601 date string.
    */
    public static parseUnlessNull(date: DateTime | DateOnly | Date | string): DateOnly {
        const hasValue = typeof date == "string"
            ? !IsNullOrWhiteSpace(date)
            : !!date;

        return hasValue
            ? new DateOnly(date)
            : null;
    }

    /**
     * Whether the date value is valid.
     */
    public get isValid(): boolean {
        return this.parsed?.isValid ?? false;
    }
    /**
    * The year number.
    */
    public get year(): number {
        return this.parsed?.year;
    }

    /**
    * The month number, where January is 1.
    */
    public get month(): number {
        return this.parsed?.month;
    }

    /**
     * The month number, where January is 0.
     */
    public get jsMonth(): number {
        return this.month
            ? this.month - 1
            : null;
    }

    /**
    * The full month name in the browser's locale (like "January").
    */
    public get monthLong(): string {
        return this.parsed?.monthLong;
    }

    /**
    ** The short month name in the browser's locale (like "Jan").
    */
    public get monthShort(): string {
        return this.parsed?.monthShort;
    }

    /**
    * The day of month number.
    */
    public get day(): number {
        return this.parsed?.day;
    }

    /**
    * The weekday number, where Monday is 1.
    */
    public get weekday(): number {
        return this.parsed?.weekday;
    }

    /**
    * The short weekday name in the browser's locale (like "Mon").
    */
    public get weekdayShort(): string {
        return this.parsed?.weekdayShort;
    }

    /**
    * The full weekday name in the browser's locale (like "Monday").
    */
    public get weekdayLong(): string {
        return this.parsed?.weekdayLong;
    }

    /**
    * Get a vanilla Date representation with only the date values set, in the current time zone *without* conversion.
    */
    public toJSDate(): Date {
        return this.isValid
            ? new Date(this.year, this.jsMonth, this.day)
            : null;
    }

    /**
    * Get the underlying Luxon DateTime.
    */
    public toLuxon(): DateTime {
        return this.parsed;
    }

    /**
    * Get a date-only ISO 8601 string.
    * @param format Override the default date formatting (see format spec at https://moment.github.io/luxon/docs/manual/tour.html#formatting-your-datetime).
    */
    public toString(format: string = null): string {
        return this.isValid
            ? this.parsed.toFormat(format ?? "yyyy-MM-dd")
            : "";
    }

    /**
     * Get a full ISO 8601 string representation for JSON serialization.
     * */
    public toJSON(): string {
        return this.parsed?.toISO();
    }

    /**
     * Get a value for math operators (===, <, >, <=, >=)
     **/
    public valueOf(): number {
        return this.parsed?.valueOf();
    }

    /**
     * Get a new date with the given interval added. Note that this discards any time remainder, so `date.plus({ hours: 24 })` is not necessarily the same as `date.plus({ hours: 12 }).plus({ hours: 12 })`.
     * @param duration The interval to add. See https://moment.github.io/luxon/docs/manual/math.html for duration object syntax.
     */
    public plus(duration: DurationObjectUnits | TimeInterval): DateOnly {
        if (!this.parsed)
            return new DateOnly(this.parsed);

        const luxonDuration = duration instanceof TimeInterval
            ? this.getLuxonDurationObjectFromTimeInterval(duration)
            : duration;

        return new DateOnly(this.parsed.plus(luxonDuration));
    }

    private getLuxonDurationObjectFromTimeInterval(timeInterval: TimeInterval): DurationObjectUnits {
        return {
            milliseconds: timeInterval.isMillisecond ? timeInterval.value : 0,
            seconds: timeInterval.isSecond ? timeInterval.value : 0,
            minutes: timeInterval.isMinute ? timeInterval.value : 0,
            hours: timeInterval.isHour ? timeInterval.value : 0,
            days: timeInterval.isDay ? timeInterval.value : 0,
            weeks: timeInterval.isWeek ? timeInterval.value : 0,
            months: timeInterval.isMonth ? timeInterval.value : 0,
            years: timeInterval.isYear ? timeInterval.value : 0
        };
    }
}

export { DateOnly };
